import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import type {ColorValue, LightType} from "#template/nodes/types"
import {colorValue, lightType} from "#template/nodes/validation"
import {LightNode} from "#template/nodes/object/light/light-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {visitNone} from "#template/template-node/visit-helpers"

export type SunLightParameters = {
    name: string
    intensity: number
    angle: number
    color: ColorValue
    on: boolean
    visibleDirectly: boolean
    visibleInReflections: boolean
    visibleInRefractions: boolean
    lightType: LightType
} & ObjectNodeParameters

const sunLightDefinition: ObjectNodeDefinition<SunLightParameters> = {
    nodeClass: "SunLight",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                intensity: z.number(),
                angle: z.number(),
                color: colorValue,
                on: z.boolean(),
                visibleDirectly: z.boolean(),
                visibleInReflections: z.boolean(),
                visibleInRefractions: z.boolean(),
                lightType: lightType,
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class SunLight extends LightNode<SunLightParameters> {
    public override get definition() {
        return sunLightDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<SunLightParameters> {
        const {evaluator} = context
        const {angle, color, on, intensity, visibleDirectly, visibleInReflections, visibleInRefractions, lightType} = this.parameters

        const scope = evaluator.getScope(this)

        const halfAngle = (angle * Math.PI) / 180 / 2
        const solidAngle = 2 * Math.PI * (1 - Math.cos(halfAngle))
        const mappedIntensity = lightType === "Corona" ? intensity * solidAngle : intensity

        this.setupObject(scope, context, "SunLight", undefined, undefined, (objectProps) => {
            return {
                preDisplayList: scope.struct<SceneNodes.SunLight>("SunLight", {
                    type: "SunLight",
                    ...objectProps,
                    angle,
                    color,
                    on,
                    intensity: mappedIntensity,
                    visibleDirectly,
                    visibleInReflections,
                    visibleInRefractions,
                }),
            }
        })

        return visitNone(this.parameters)
    }
}
